import { HintAnimation } from '../../portal-hints/hint-motion';
import { abs, bar, C, chip, field, glass, hline, icon, okChip, panel, pill, pressed, StoryScene, text } from './story-scene';

/**
 * Vorschau-Szenen des Themas 9 „Creator“: je Lektion EINE Szene. Der
 * Kurs-Editor läuft im Portal in einem eingebetteten Rahmen, den die
 * Klickpfade nicht erreichen — was darin geschieht (gestalten, teilen,
 * kommentieren), zeigen deshalb diese Filme. Begriffe wie im Creator.
 * Panel-Koordinaten ab (20,18); der Zeiger rechnet in Bühnenkoordinaten.
 */

const hidden = (html: string) => html.replace('style="', 'style="opacity:0;');
const box = (x: number, y: number, w: number, h: number, inner: string, opacity = 0.6) =>
  glass(16, opacity, `left:${x}px;top:${y}px;width:${w}px;height:${h}px;`, inner);
/** Gestrichelte Ablagefläche wie „… oder starte mit vorhandenen Materialien“. */
const dropZone = (x: number, y: number, w: number, h: number, inner: string) =>
  `<div style="${abs(x, y, w, h, 'border-radius:16px;border:1.5px dashed rgb(83 109 145 / .45);background:rgb(255 255 255 / .25);')}">${inner}</div>`;
/** Kleine Dateikarte (Rise-Paket, Dokument). */
const fileCard = (x: number, y: number, name: string, ref: string) =>
  `<div data-r="${ref}" style="${abs(x, y, 190, 40, 'border-radius:12px;background:rgb(255 255 255 / .9);box-shadow:0 8px 18px -8px rgb(38 65 112 / .45);display:flex;align-items:center;gap:8px;padding:0 12px;box-sizing:border-box;')}">${icon('file', 18, C.action)}<span style="font-size:12px;font-weight:600;color:${C.text};white-space:nowrap;">${name}</span></div>`;
/** Inhaltsblock im Editor: graue Textzeilen. */
const lines = (x: number, y: number, w: number, n: number) =>
  Array.from({ length: n }, (_, i) => `<div style="${abs(x, y + i * 12, i === n - 1 ? w * 0.6 : w, 6, 'border-radius:3px;background:rgb(83 109 145 / .22);')}"></div>`).join('');

/** 9.1 — Kurs, Übung oder Material; alles landet in der Bibliothek. */
const creatorOverview = (): HintAnimation =>
  new StoryScene({
    seconds: 12,
    still: 10.5,
    html: panel(
      `<div data-r="start">` +
        text(30, 22, 200, 'Creator', 15, 600) + chip(470, 22, 100, 'Bibliothek') +
        `<div data-r="t1" style="opacity:0;">${box(60, 64, 230, 96, `<div style="${abs(103, 14, 24, 24)}">${icon('book', 22, C.action)}</div>` + text(14, 46, 202, 'Neuen Kurs erstellen', 13, 600, C.text, 'text-align:center;') + text(14, 68, 202, 'E-Learning (SCORM) oder Moodle', 11, 500, C.muted, 'text-align:center;'))}</div>` +
        `<div data-r="t2" style="opacity:0;">${box(310, 64, 230, 96, `<div style="${abs(103, 14, 24, 24)}">${icon('template', 22, C.action)}</div>` + text(14, 46, 202, 'Neue Übung erstellen', 13, 600, C.text, 'text-align:center;') + text(14, 68, 202, 'Aus Thema oder Wissensbasis', 11, 500, C.muted, 'text-align:center;'))}</div>` +
        `<div data-r="drop" style="opacity:0;">${dropZone(60, 176, 480, 96,
          text(0, 14, 480, '… oder starte mit vorhandenen Materialien.', 12.5, 600, C.text, 'text-align:center;') +
          chip(82, 52, 70, 'Rise') + chip(162, 52, 70, 'Word') + chip(242, 52, 96, 'PowerPoint') + chip(348, 52, 56, 'PDF'))}</div>` +
      `</div>` +
      `<div data-r="lib" style="opacity:0;">` +
        text(30, 22, 200, 'Bibliothek', 15, 600) + chip(30, 56, 52, 'Alle') + chip(90, 56, 74, 'Übungen') + chip(172, 56, 64, 'Kurse') +
        hline(30, 94, 540) +
        [['Brandschutz im Büro', 'E-Learning'], ['Kundenservice am Telefon', 'Microlearning'], ['Quiz Datenschutz', 'Übung']]
          .map(([title, kind], i) =>
            `<div data-r="row${i}" style="opacity:0;">${text(30, 110 + i * 48, 250, title, 13, 600)}${text(290, 110 + i * 48, 120, kind, 12, 500, C.muted)}${chip(410, 108 + i * 48, 64, 'Entwurf')}` +
            `<div style="${abs(494, 106 + i * 48, 76, 28, `border-radius:999px;box-shadow:inset 0 0 0 1.5px ${C.action};display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:${C.action};`)}">Öffnen</div>` +
            `${hline(30, 146 + i * 48, 540)}</div>`)
          .join('') +
      `</div>`,
      null,
    ),
    tracks: [
      { r: 't1', at: [0.4, 0.9], o: [0, 1], y: [10, 0] },
      { r: 't2', at: [0.9, 1.4], o: [0, 1], y: [10, 0] },
      { r: 'drop', at: [1.6, 2.1], o: [0, 1] },
      { r: 'start', at: [5.4, 5.8], o: [1, 0] },
      { r: 'lib', at: [5.9, 6.3], o: [0, 1] },
      { r: 'row0', at: [6.4, 6.8], o: [0, 1], x: [12, 0] },
      { r: 'row1', at: [6.8, 7.2], o: [0, 1], x: [12, 0] },
      { r: 'row2', at: [7.2, 7.6], o: [0, 1], x: [12, 0] },
    ],
    cursor: { at: [0, 2.4, 4.2, 5.4, 8.4, 12], x: [300, 300, 540, 540, 552, 552], y: [300, 260, 52, 52, 142, 142], clicks: [4.8, 9.0] },
  });

/** 9.2 — Rise-Paket ablegen, übernehmen lassen, prüfen, öffnen. */
const creatorImport = (): HintAnimation =>
  new StoryScene({
    seconds: 13,
    still: 11.5,
    html: panel(
      text(30, 22, 300, 'Creator', 15, 600) +
        `<div data-r="zone">${dropZone(60, 64, 480, 110, text(0, 36, 480, '… oder starte mit vorhandenen Materialien.', 12.5, 600, C.text, 'text-align:center;') + text(0, 60, 480, 'Rise (SCORM, .zip) · Word · PowerPoint · PDF · Markdown', 11, 500, C.muted, 'text-align:center;'))}</div>` +
        fileCard(380, 200, 'Brandschutz_Rise.zip', 'file') +
        `<div data-r="dlg" style="opacity:0;">${box(110, 52, 380, 236,
          text(20, 16, 340, 'Rise-Kurs importieren', 15, 600) +
          `<div data-r="up">${text(20, 56, 340, 'Paket wird hochgeladen …', 12.5, 500, C.muted)}${bar(20, 84, 340, 'prog')}</div>` +
          `<div data-r="take" style="opacity:0;">${text(20, 56, 340, 'Der Kurs wird übernommen …', 12.5, 500, C.muted)}</div>` +
          `<div data-r="done" style="opacity:0;">` +
            okChip(20, 104, 270, 'Fertig: 6 Lektionen mit 48 Blöcken') +
            chip(20, 138, 230, '2 Blöcke im Editor prüfen') +
            pill(220, 186, 140, 'Kurs öffnen') +
          `</div>`,
          0.85)}</div>`,
      null,
    ),
    tracks: [
      { r: 'file', at: [0.6, 2.0, 2.3], x: [0, -150, -150], y: [0, -90, -90], o: [1, 1, 0] },
      { r: 'zone', at: [2.3, 2.7], o: [1, 0.15] },
      { r: 'dlg', at: [2.4, 2.8], o: [0, 1], y: [8, 0] },
      { r: 'prog', at: [2.8, 5.0], s: [0, 1] },
      { r: 'up', at: [5.2, 5.5], o: [1, 0] },
      { r: 'take', at: [5.5, 5.8, 7.2, 7.4], o: [0, 1, 1, 0] },
      { r: 'done', at: [7.4, 7.8], o: [0, 1], y: [6, 0] },
    ],
    cursor: {
      at: [0, 0.6, 2.0, 2.6, 8.2, 9.4, 13],
      x: [520, 505, 355, 355, 420, 420, 420],
      y: [300, 238, 148, 148, 272, 272, 272],
      clicks: [0.6, 8.8],
    },
  });

/** 9.3 — Art, Titel und Look wählen; dann aus Blöcken aufbauen. */
const creatorDesign = (): HintAnimation =>
  new StoryScene({
    seconds: 14,
    still: 12.5,
    html: panel(
      `<div data-r="dlg">${box(110, 20, 380, 280,
        text(20, 14, 340, 'Kurs von Hand bauen', 15, 600) +
        text(20, 46, 100, 'Art', 11, 700, C.muted) +
        pressed(20, 64, 120, 30) + text(20, 70, 120, 'E-Learning', 12, 600, C.text, 'text-align:center;') + text(144, 70, 120, 'Microlearning', 12, 500, C.muted, 'text-align:center;') +
        text(20, 106, 100, 'Titel', 11, 700, C.muted) + field(20, 124, 340, '') +
        `<div data-r="title" style="opacity:0;">${text(36, 132, 300, 'Brandschutz im Büro', 13, 500)}</div>` +
        text(20, 168, 100, 'Look', 11, 700, C.muted) +
        `<div style="${abs(20, 186, 160, 44, 'border-radius:6px;box-shadow:inset 0 0 0 1px rgb(83 109 145 / .3);background:rgb(255 255 255 / .6);')}">${text(12, 12, 130, 'Classic', 12.5, 600)}</div>` +
        `<div style="${abs(200, 186, 160, 44, 'border-radius:16px;box-shadow:inset 0 0 0 1px rgb(83 109 145 / .3);background:rgb(255 255 255 / .6);')}">${text(12, 12, 100, 'Rounded', 12.5, 600)}</div>` +
        `<div data-r="pick" style="opacity:0;${abs(200, 186, 160, 44, `border-radius:16px;box-shadow:inset 0 0 0 2px ${C.action};`)}">${icon('check', 16, C.action, 2.4, 'position:absolute;right:12px;top:14px;')}</div>` +
        pill(220, 240, 140, 'Kurs anlegen', 'create'),
        0.85)}</div>` +
      `<div data-r="ed" style="opacity:0;">` +
        text(20, 14, 300, 'Brandschutz im Büro', 13, 600) + `<div data-r="saved" style="opacity:0;">${okChip(468, 12, 112, 'Gespeichert')}</div>` + hline(10, 44, 580) +
        text(20, 58, 120, 'Gliederung', 11, 600, C.muted) + text(20, 82, 120, 'Blöcke', 11, 700, C.action) + text(20, 106, 120, 'Medien', 11, 600, C.muted) +
        `<div style="${abs(130, 52, 2, 250, 'background:rgb(83 109 145 / .14);')}"></div>` +
        `<div style="${abs(150, 54, 300, 248, 'border-radius:10px;background:rgb(255 255 255 / .75);')}"></div>` +
        text(168, 64, 260, 'Lektion 1 · Brandschutz im Büro', 13, 600) +
        `<div data-r="b1" style="opacity:0;">${lines(168, 94, 264, 3)}</div>` +
        `<div data-r="b2" style="opacity:0;${abs(168, 136, 264, 72, 'border-radius:8px;background:linear-gradient(120deg, #f9a8d4 0%, #a5b4fc 55%, #bae6fd 100%);')}"></div>` +
        `<div data-r="b3" style="opacity:0;">${chip(168, 220, 120, 'Wissensfrage')}${lines(168, 254, 264, 2)}</div>` +
        `<div style="${abs(468, 52, 2, 250, 'background:rgb(83 109 145 / .14);')}"></div>` +
        text(484, 58, 100, 'Design', 11, 700, C.action) + text(484, 84, 100, 'Look „Rounded“', 11, 500, C.muted) +
        `<div style="${abs(484, 108, 96, 24, `border-radius:8px;background:${C.action};`)}"></div>` +
      `</div>`,
      null,
    ),
    tracks: [
      { r: 'title', at: [1.2, 1.8], o: [0, 1] },
      { r: 'pick', at: [2.8, 3.1], o: [0, 1] },
      { r: 'create', at: [4.3, 4.45, 4.6], s: [1, 0.94, 1] },
      { r: 'dlg', at: [5.0, 5.4], o: [1, 0] },
      { r: 'ed', at: [5.4, 5.8], o: [0, 1] },
      { r: 'b1', at: [6.6, 7.0], o: [0, 1], y: [8, 0] },
      { r: 'b2', at: [8.0, 8.4], o: [0, 1], y: [8, 0] },
      { r: 'b3', at: [9.4, 9.8], o: [0, 1], y: [8, 0] },
      { r: 'saved', at: [10.6, 11.0], o: [0, 1] },
    ],
    cursor: {
      at: [0, 0.8, 2.2, 3.4, 4.6, 6.0, 7.6, 9.0, 14],
      x: [400, 300, 450, 450, 420, 72, 72, 72, 72],
      y: [300, 178, 240, 292, 292, 108, 108, 108, 108],
      clicks: [1.0, 2.8, 4.4, 6.4, 7.8, 9.2],
    },
  });

/** 9.4 — Teilen, Link schicken, Gast kommentiert; umsetzen und abhaken. */
const creatorReview = (): HintAnimation =>
  new StoryScene({
    seconds: 16,
    still: 14.5,
    html: panel(
      // Phase 1 — im Editor: Teilen → Vorschaulink
      `<div data-r="share">` +
        text(20, 14, 260, 'Brandschutz im Büro', 13, 600) +
        chip(330, 12, 80, 'Vorschau') +
        `<div data-r="teilen" style="${abs(420, 12, 74, 24, `border-radius:999px;box-shadow:inset 0 0 0 1.5px ${C.action};display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:${C.action};transform-origin:50% 50%;`)}">Teilen</div>` +
        `<div style="${abs(502, 12, 82, 24, 'border-radius:999px;background:#243650;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:#fff;')}">Exportieren</div>` +
        hline(10, 44, 580) +
        `<div data-r="link" style="opacity:0;">${box(120, 70, 360, 200,
          text(20, 16, 320, 'Vorschaulink teilen', 15, 600) +
          text(20, 46, 200, 'Name', 11, 700, C.muted) + field(20, 64, 210, '') + text(36, 72, 190, 'Runde 1 – Kunde', 12.5, 500) +
          text(244, 46, 100, 'Gültig', 11, 700, C.muted) + chip(244, 69, 96, '30 Tage') +
          pill(200, 116, 140, 'Link erstellen', 'make') +
          `<div data-r="copied" style="opacity:0;">${okChip(20, 160, 200, 'Link kopiert')}</div>`,
          0.9)}</div>` +
      `</div>` +
      // Phase 2 — der Gast sieht den gespeicherten Stand und kommentiert
      `<div data-r="guest" style="opacity:0;">` +
        chip(20, 14, 150, 'Vorschau · Gast') + hline(10, 44, 580) +
        `<div style="${abs(40, 62, 340, 230, 'border-radius:10px;background:rgb(255 255 255 / .8);')}"></div>` +
        text(60, 76, 300, 'Im Brandfall', 14, 600) +
        `<div data-r="mark" style="opacity:0;${abs(58, 106, 244, 18, 'border-radius:4px;background:rgb(255 200 100 / .55);')}"></div>` +
        text(60, 106, 300, 'Ruhe bewahren und den Raum verlassen.', 12, 500) +
        lines(60, 138, 300, 4) +
        `<div data-r="gc" style="opacity:0;">${box(396, 92, 190, 96,
          text(12, 10, 166, 'Kundin · Gast', 11, 700, C.muted) +
          `<div style="${abs(12, 32, 166, 56, `font-size:12px;line-height:1.4;color:${C.text};white-space:normal;`)}">Bitte noch die Notrufnummer 112 ergänzen.</div>`,
          0.85)}</div>` +
      `</div>` +
      // Phase 3 — Hinweis in der Glocke, umsetzen, antworten, erledigt
      `<div data-r="author" style="opacity:0;">` +
        `<div style="${abs(30, 20, 22, 22, 'border-radius:999px;background:#FFC864;display:flex;align-items:center;justify-content:center;')}">${icon('bell', 13, '#8A5A00', 2)}</div>` +
        text(62, 21, 400, 'Kommentar über deinen Vorschaulink', 13, 600) + hline(10, 54, 580) +
        text(30, 70, 200, 'Kommentare', 12, 700, C.muted) +
        box(30, 92, 300, 150,
          text(14, 10, 270, 'Kundin · Gast · Runde 1 – Kunde', 11, 700, C.muted) +
          `<div style="${abs(14, 30, 272, 36, `font-size:12px;line-height:1.4;color:${C.text};white-space:normal;`)}">Bitte noch die Notrufnummer 112 ergänzen.</div>` +
          `<div data-r="reply" style="opacity:0;">${text(14, 76, 270, 'Du: Ist ergänzt, danke!', 12, 600, C.action)}</div>` +
          `<div data-r="doneC" style="opacity:0;">${okChip(14, 110, 100, 'Erledigt')}</div>`,
          0.8) +
        `<div style="${abs(356, 92, 224, 150, 'border-radius:10px;background:rgb(255 255 255 / .8);')}"></div>` +
        text(372, 104, 200, 'Im Brandfall', 13, 600) +
        `<div data-r="fix" style="opacity:0;">${text(372, 130, 200, 'Notruf: 112', 12, 600, C.okInk)}</div>` +
        lines(372, 158, 190, 3) +
      `</div>`,
      null,
    ),
    tracks: [
      { r: 'teilen', at: [1.0, 1.15, 1.3], s: [1, 0.94, 1] },
      { r: 'link', at: [1.4, 1.8], o: [0, 1], y: [8, 0] },
      { r: 'make', at: [3.0, 3.15, 3.3], s: [1, 0.94, 1] },
      { r: 'copied', at: [3.4, 3.8], o: [0, 1] },
      { r: 'share', at: [4.8, 5.2], o: [1, 0] },
      { r: 'guest', at: [5.3, 5.7, 9.2, 9.6], o: [0, 1, 1, 0] },
      { r: 'mark', at: [6.3, 6.8], o: [0, 1] },
      { r: 'gc', at: [7.2, 7.6], o: [0, 1], x: [10, 0] },
      { r: 'author', at: [9.7, 10.1], o: [0, 1] },
      { r: 'fix', at: [11.0, 11.4], o: [0, 1] },
      { r: 'reply', at: [12.0, 12.4], o: [0, 1] },
      { r: 'doneC', at: [13.0, 13.4], o: [0, 1], y: [6, 0] },
    ],
    cursor: {
      at: [0, 0.8, 1.6, 2.8, 4.0, 6.0, 7.0, 9.4, 12.6, 14.0, 16],
      x: [300, 477, 477, 410, 410, 80, 300, 300, 110, 110, 110],
      y: [300, 42, 42, 222, 222, 124, 124, 124, 238, 238, 238],
      clicks: [1.1, 3.1, 6.2, 13.0],
    },
  });

/** Szenen des Themas 9, je ID (Szenen-ID = Lektions-ID). */
export const CREATOR_SCENES = {
  'creator-overview': creatorOverview,
  'creator-import': creatorImport,
  'creator-design': creatorDesign,
  'creator-review': creatorReview,
} satisfies Record<string, () => HintAnimation>;
